import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Divider,
  PieChartWithLegend,
  ProgressList,
  Tooltip,
  Typography,
} from '@scope/scope-one-ui/core';
import Info from 'assets/icons/info.svg';
import { theme } from 'theme';
import { COLORS, SIZES } from '@scope/scope-one-ui/theme';
import { Cell, ChartTitle, Grid } from './ESGMaterialityMap.styled';
import { MaterialityMap } from '../types';
import { ESGMaterialityMapProps } from './ESGMaterialityMap.d';

const getData = (mapData: MaterialityMap, chartData: MaterialityMap) => {
  const { chartShort } = theme.customPalettes;

  const getMinMax = (values: number[]) => ({
    min: Math.min(...values) * 1.25,
    max: Math.max(...values) * 1.25,
  });

  const parse = (variant: 'e' | 's' | 'g') => ({
    title: { e: 'Environment', s: 'Social', g: 'Governance' }[variant],
    indicators: mapData[variant].map(({ label, value }) => ({
      label,
      count: value * 100,
      color: chartShort[0],
    })),
    ...getMinMax(mapData[variant].map(({ value }) => value * 100)),
    chart: chartData[variant].map(({ label, value }, index) => ({
      name: label,
      value: value * 100,
      color: chartShort[index],
    })),
  });

  return Object.entries({
    environment: parse('e'),
    social: parse('s'),
    governance: parse('g'),
  });
};

export const ESGMaterialityMap: FC<ESGMaterialityMapProps> = ({ mapData, chartData }) => {
  const { t } = useTranslation();

  const data = getData(mapData, chartData);

  return (
    <Box display="flex" flexDirection="column" p={2} gridGap={2}>
      <Box display="flex" my={1} gridGap={2} alignItems="center">
        <Typography variant="sectionHeader" color={COLORS.typography.label}>
          {t('Materiality map')}
        </Typography>
        <Tooltip
          position="right"
          content={
            <Box display="flex" flexDirection="column">
              <Typography>
                {t(
                  'Percentage of impact by indicator is measured as share of total company revenue.',
                )}
              </Typography>
              <Typography>
                {t(
                  'Value chain distributions are displayed as shares adding up to 100% per E-S-G dimension.',
                )}
              </Typography>
            </Box>
          }
        >
          <Info width={SIZES.spacing(2)} height={SIZES.spacing(2)} color={COLORS.typography.main} />
        </Tooltip>
      </Box>
      <Divider />
      <Grid>
        {data.map(([key, { title, indicators, min, max }]) => (
          <Cell>
            <Box display="flex" flexDirection="column">
              <Box display="flex" p={2}>
                <Typography variant="h2" color={COLORS.typography.label}>
                  {title}
                </Typography>
              </Box>
              <ProgressList
                key={`${key}_indicators`}
                percentage
                total={max}
                min={min}
                colored
                data={indicators}
                numberFormatOptions={{ maximumFractionDigits: 1 }}
              />
            </Box>
          </Cell>
        ))}
        {data.map(([key, { chart }]) => (
          <Cell key={`${key}_chart`} display="flex" flexDirection="column" p={2} gridGap={2}>
            <ChartTitle display="flex" pt={4}>
              <Typography color={COLORS.typography.main} variant="body2">
                COMPANY VERSUS VALUE-CHAIN IMPACT
              </Typography>
            </ChartTitle>
            <PieChartWithLegend chartData={chart} layout="horizontal" />
          </Cell>
        ))}
      </Grid>
    </Box>
  );
};
